import { DateTime, Interval } from 'luxon';
import { transformToCamelCaseKeys } from 'common/utils/dataManipulation';

type openingHoursObject = {
  closeAt: string;
  day: string;
  offDay: boolean;
  openAt: string;
};

type openingHoursObjectFormatted = {
  closeAt: string;
  commonDays: Array<string>;
  offDay: boolean;
  openAt: string;
};

export const getOpenAtAndCloseAtDates: (date: DateTime, openAt: string, closeAt: string) => Array<DateTime> = (
  date,
  openAt,
  closeAt,
) => {
  const splitOpenAt = openAt.split(':');

  const openAtDate = date.set({
    hour: parseInt(splitOpenAt[0]),
    minute: parseInt(splitOpenAt[1]),
  });

  const splitCloseAt = closeAt.split(':');

  let closeAtDate = date.set({
    hour: parseInt(splitCloseAt[0]),
    minute: parseInt(splitCloseAt[1]),
  });

  if (openAtDate.diff(closeAtDate).toObject().milliseconds > 0) {
    closeAtDate = closeAtDate.plus({ days: 1 });
  }
  return [openAtDate, closeAtDate];
};

export const getOpeningHoursByDay = openingHours => {
  const transformedHours = openingHours && Object.values(openingHours).map(item => transformToCamelCaseKeys(item));
  return transformedHours?.reduce((acc, { day, offDay, openAt, closeAt }) => {
    // TODO: Remove openAtOffset/closeAtOffset once data issue #PEG-11335 is solved
    const openAtOffset = openAt === '24:00' ? '00:00' : openAt;
    const closeAtOffset = closeAt === '24:00' ? '00:00' : closeAt;
    return {
      ...acc,
      [day]: { offDay, openAt: openAtOffset, closeAt: closeAtOffset },
    };
  }, {});
};

export const isStoreClosedAtDate: (
  openingHours: Array<openingHoursObject>,
  restaurantTimeZone: string,
  date: DateTime,
) => boolean = (openingHours, restaurantTimeZone, date) => {
  if (openingHours) {
    const openingHoursByDay = getOpeningHoursByDay(openingHours);
    const dateObj = date.setLocale('en').setZone(restaurantTimeZone);

    const yesterday = dateObj.minus({ days: 1 });

    const dayName = dateObj.toFormat('ccc').toLowerCase();

    const yesterdayName = yesterday.toFormat('ccc').toLowerCase();

    const { offDay, openAt, closeAt } = openingHoursByDay[dayName];

    if (offDay) {
      return true;
    }

    // TODO: check with BE and check why false
    if (openAt === closeAt) {
      return false;
    }

    const { offDay: yesterdayOffDay, openAt: yesterDayOpenAt, closeAt: yesterdayCloseAt } = openingHoursByDay[yesterdayName];

    const [openAtDate, closeAtDate] = getOpenAtAndCloseAtDates(dateObj, openAt, closeAt);
    const [yesterdayOpenAtDate, yesterdayCloseAtDate] = getOpenAtAndCloseAtDates(yesterday, yesterDayOpenAt, yesterdayCloseAt);

    const yesterdayInterval = Interval.fromDateTimes(yesterdayOpenAtDate, yesterdayCloseAtDate);

    if (!yesterdayOffDay && yesterdayInterval.contains(dateObj)) {
      return false;
    }

    const interval = Interval.fromDateTimes(openAtDate, closeAtDate);
    return !interval.contains(dateObj);
  }
  return null;
};

// TODO we need to remove setting zone
export const isBranchClosed: (openingHours: Array<openingHoursObject>, restaurantTimeZone?: string) => boolean = (
  openingHours,
  restaurantTimeZone,
) => isStoreClosedAtDate(openingHours, restaurantTimeZone, DateTime.local().setLocale('en').setZone(restaurantTimeZone));

export const getOpeningHoursByTime: (openingHours: Array<openingHoursObject>) => Array<openingHoursObjectFormatted> =
  openingHours => {
    if (openingHours) {
      const openingHoursByTime = openingHours.reduce((acc, curr, idx) => {
        const { day, offDay, openAt, closeAt } = curr;
        // TODO: Remove openAtOffset/closeAtOffset once data issue #PEG-11335 is solved
        const openAtOffset = openAt === '24:00' ? '00:00' : openAt;
        const closeAtOffset = closeAt === '24:00' ? '00:00' : closeAt;

        const { offDay: prevOffDay, openAt: prevOpenAt, closeAt: prevCloseAt } = openingHours[idx - 1] || {};
        // TODO: Remove prevOpenAtOffset/prevCloseAtOffset once data issue #PEG-11335 is solved
        const prevOpenAtOffset = prevOpenAt === '24:00' ? '00:00' : prevOpenAt;
        const prevCloseAtOffset = prevCloseAt === '24:00' ? '00:00' : prevCloseAt;

        const haveSameWorkingHours = offDay
          ? prevOffDay === offDay
          : !prevOffDay && openAtOffset === prevOpenAtOffset && closeAtOffset === prevCloseAtOffset;

        if (haveSameWorkingHours && acc.length > 0) {
          acc[acc.length - 1].commonDays.push(day);
          return acc;
        }
        return [...acc, { offDay, openAt: openAtOffset, closeAt: closeAtOffset, commonDays: [day] }];
      }, []);
      return openingHoursByTime;
    }
    return null;
  };
